// src/components/ManageDealDrawer/ManageDealDrawer.tsx

import React, { useEffect, useRef, useState } from 'react';
import Button from '../_custom/Button/Button';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import SideDrawer from '../_custom/SideDrawer/SideDrawer';
import Form from '../_custom/Form/Form';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import { DEBT_PRODUCT_FILTERS, PEC_PICKER_LABELS, PEC_REMOVE_ALL_CONFIRM } from './DealDrawerConstants';

// Redux imports
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { setShowCommentsThread, resetAndHideCommentsThread, closeCommentsThread } from '../../store/commentsThread/commentsThread_actions';
import { COMMENTS_THREAD_SOURCES } from '../../store/commentsThread/commentsThread_reducer';

// Assets
import { commentsIcon } from '../../assets/hostedassets';

// Custom hooks
import { useManageDealDrawer } from './useManageDealDrawer';

// Subcomponents
import { ManageDealDrawerHeader } from './Components/ManageDealDrawerHeader';
import { ManageDealDrawerFooter } from './Components/ManageDealDrawerFooter/ManageDealDrawerFooter';
import FormSideStepper from '../FormSideStepper/FormSideStepper';
import { FormSection } from './Components/Sections/FormSection';
import { ApplicationFormStep } from './Components/Sections/ApplicationFormStep';
import { FieldPicker } from './Components/FieldPicker/FieldPicker';
import { StepEmptyState } from './Components/StepEmptyState/StepEmptyState';

// Styles
import styles from './ManageDealDrawer.module.scss';
import ConfigurableModal from '../ConfigurableModal/ConfigurableModal';

type Props = {
    source: string;
    onClose?: Function;
    dealId?: string;
    saveSelection?: Function;
    fetchCompanyMetrics?: Function;
    dealStatus?: string;
};

const ManageDealDrawer: React.FC<Props> = ({ source, onClose, dealId, saveSelection, fetchCompanyMetrics, dealStatus }) => {
    const dispatch = useAppDispatch();
    const routeParams = useParams();

    const {
        formData,
        originalFormData,
        handleChange,
        dealDrawerState,
        lenderList,
        loanTypeOptions,
        sectorList,
        negativeSectorList,
        step,
        disableSecondary,
        loadingPrefilledData,
        formConfig,
        stepperConfig,
        investorName,
        handlePrimary,
        handleSecondary,
        handleStepperClick,
        isFromComplete,
        dealDrawerBodyRef,
        stepContainerRef,
        auditedfinancialsMapping,
        isWebsiteValid,
        setIsWebsiteValid,
        isWebsitePrefilled,
        isLinkedInValid,
        getInputValidation,
        getButtonIcon,
        getButtonText,
        validateWebsite,
        negativeLenderList,
        connectedLenderNames,
        getConvertedValue,
        params,
        setParams,
        handleSkipMandate,
        handleUpdateMandate,
        showDismissalAlert,
        setShowDismissalAlert,
        handleDrawerClose,
        handleSaveAndClose,
        handleCloseWithoutSave,
        citiesList,
        drawerOrgId,
        activeFinancialsTab,
        handleFinancialsTabChange,
        hasUnsavedChanges,
        pecStepSection,
        pecFilteredSection,
        pecPickerOptions,
        pecSelectedFields,
        pecAlwaysOnFields,
        isStepConfigured,
        isPecStep,
        isPartnerFacilityStep,
        isMandateApplicantFormStep,
        visibleSections,
        visibleSectionIds,
        sectionDisablePrimarySetter,
        disablePrimaryWithSections,
        handleClearField,
        handleRemoveAllForStep,
        handleClearAllForStep,
        handlePickerSelectionChange,
        showRemoveAllConfirm,
        setShowRemoveAllConfirm,
        isPickerOpen,
        setIsPickerOpen,
        coApplicants,
        removeCoApplicantRow,
        updateCoApplicantField,
        primaryApplicantLink,
        primarySlotActive,
        existingDirectors,
        applicantPickerSelectedIds,
        handleApplicantPickerChange,
        handleAddApplicant,
        clearPrimaryApplicant,
        applicantPrimaryTypeError,
        duplicateApplicantError,
    } = useManageDealDrawer({ source, onClose, dealId, saveSelection, fetchCompanyMetrics, dealStatus });

    const assesseeOrgId = routeParams.id || dealDrawerState.companyId;

    // Comments thread state and actions
    const commentsThreadState = useAppSelector((state) => state.commentsThreadReducer);

    // Toggle function for comments thread
    const showHideCommentsThread = () => {
        if (commentsThreadState.showCommentsThread) {
            dispatch(closeCommentsThread());
        } else {
            if (!assesseeOrgId) {
                toast.error('Failed to initialize comments thread');
                return;
            }
            dispatch(
                setShowCommentsThread({
                    source: COMMENTS_THREAD_SOURCES.EDIT_PROFILE,
                    assesseeOrgId: assesseeOrgId,
                }),
            );
        }
    };

    // Cleanup comments thread on unmount
    useEffect(() => {
        return () => {
            dispatch(resetAndHideCommentsThread());
        };
    }, [dispatch]);

    // Create toggle button for header
    const commentsToggleButton = (
        <div
            className={clsx(styles.CommentsIconContainer, {
                [styles.CommentsIconContainer_Active]: commentsThreadState.showCommentsThread && !commentsThreadState.closingCommentsThread,
            })}
            onClick={showHideCommentsThread}
            title="Toggle Comments"
        >
            <img src={commentsIcon} alt="comments" height={'25px'} />
        </div>
    );

    // Determine the config for the current step
    const currentConfig = formConfig?.[step - 1];

    const canRemoveFields = pecSelectedFields.length > 0;
    // Derived as the footer derives it, so the gate and the CTA it disables agree on the last step,
    // which the gate must exempt: a zero-field PEC step is a valid selection, and only Proceed
    // needs blocking - Save & Close already just closes when nothing changed.
    const isLastStep = step >= (formConfig?.length || 1);
    const pecStepGateDisabled = !!dealDrawerState?.isPec && !isStepConfigured && !hasUnsavedChanges && !isLastStep;
    const pecLoanTypesMissing = !!dealDrawerState?.isPec && !(formData?.['preferredTerms.loanTypes']?.length > 0);

    // Shared FormSection pass-through bag for the Applicant/Co-applicant blocks - both render
    // FormSection directly (outside the generic config-section map) and need the same props.
    const formSectionPassThroughProps = {
        savedFormData: originalFormData,
        dealDrawerState,
        lenderList,
        loanTypeOptions,
        sectorList,
        negativeSectorList,
        source,
        auditedfinancialsMapping,
        isWebsiteValid,
        isWebsitePrefilled,
        setIsWebsiteValid,
        getInputValidation,
        getButtonIcon,
        getButtonText,
        validateWebsite,
        negativeLenderList,
        connectedLenderNames,
        getConvertedValue,
        citiesList,
        drawerOrgId,
        activeFinancialsTab,
        handleFinancialsTabChange,
    };

    const confirmRemoveAll = () => {
        handleRemoveAllForStep();
        setShowRemoveAllConfirm(false);
    };

    // 1) Keep track of the previous step
    const prevStepRef = useRef<number>(step);

    // 2) We'll store our animation class in local state
    const [animationClass, setAnimationClass] = useState<string>('');
    useEffect(() => {
        if (step > prevStepRef.current) {
            // Step is going from lower to higher => Slide down
            setAnimationClass('slideUp');
        } else if (step < prevStepRef.current) {
            // Step is going from higher to lower => Slide up
            setAnimationClass('slideDown');
        }

        // Save the new step as the "previous" step
        prevStepRef.current = step;
        const timer = setTimeout(() => {
            setAnimationClass('fadeIn');
        }, 300);

        return () => clearTimeout(timer);
    }, [step]);

    const sideDrawerContent = (
        <SideDrawer
            allowClose={false}
            dontClose
            width={dealDrawerState.isPec ? '75%' : source === DEBT_PRODUCT_FILTERS ? '40%' : '67%'}
            onClose={handleDrawerClose}
        >
            <div style={{ display: 'flex', height: '100%', width: '100%' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                    <ManageDealDrawerHeader
                        onClose={handleDrawerClose}
                        drawerLabel={dealDrawerState?.drawerLabel ?? 'Create Deal'}
                        investorName={dealDrawerState.companyName ?? investorName}
                        headerAction={dealDrawerState?.drawerLabel === 'Edit Profile' ? commentsToggleButton : null}
                        source={source}
                    />

                    {currentConfig && !loadingPrefilledData ? (
                        <Form>
                            <div className={styles.ManageDealDrawer} ref={dealDrawerBodyRef}>
                                <div className={styles.DealsFormWrapper}>
                                    <FormSideStepper
                                        hideStepper={stepperConfig.length === 0}
                                        step={step}
                                        stepperConfig={stepperConfig}
                                        onStepClick={handleStepperClick}
                                        statusTrigger={!disablePrimaryWithSections && !pecLoanTypesMissing}
                                        lockFutureStepsWhenInvalid={!!dealDrawerState?.isPec}
                                        isViewOnly={dealDrawerState?.readonly ?? false}
                                        checkCompleteForm={isFromComplete}
                                        isWebsiteValid={isWebsiteValid}
                                        isLinkedInValid={isLinkedInValid}
                                        source={source}
                                    />

                                    {/* Step Container */}
                                    <div className={`${styles.Step} ${animationClass ? styles[animationClass] : ''}`}>
                                        <div
                                            style={
                                                source === DEBT_PRODUCT_FILTERS
                                                    ? {
                                                          boxShadow: 'none',
                                                          background: 'transparent',
                                                          padding: 0,
                                                      }
                                                    : {}
                                            }
                                            className={styles.StepContainer}
                                            ref={stepContainerRef}
                                        >
                                            {currentConfig?.text && dealDrawerState.readonly && (
                                                <div className={styles.SectionHeading}>
                                                    {/* Show the step number only on the first section within that step */}
                                                    {`${step}.`} {currentConfig?.text}
                                                </div>
                                            )}

                                            {/* Optionally, show a subheading or instructions for the first section */}
                                            {currentConfig?.subText && dealDrawerState.readonly && (
                                                <div className={styles.StepHeading}>
                                                    <span className={styles.StepHeadingSecondaryText}>{currentConfig.subText}</span>
                                                </div>
                                            )}
                                            {isPecStep && !isPartnerFacilityStep && (
                                                <>
                                                    <div className={styles.PecStepHeader}>
                                                        <div className={styles.SectionHeading}>
                                                            {`${step}.`} {pecStepSection?.text}
                                                        </div>
                                                        <Button
                                                            type="button"
                                                            variant="tertiary"
                                                            text={PEC_PICKER_LABELS.removeAll}
                                                            className={styles.PecTextButton}
                                                            disabled={!canRemoveFields}
                                                            onClick={() => setShowRemoveAllConfirm(true)}
                                                        />
                                                    </div>

                                                    <FieldPicker
                                                        options={pecPickerOptions}
                                                        selected={pecSelectedFields}
                                                        onSelectionChange={handlePickerSelectionChange}
                                                        open={isPickerOpen}
                                                        onOpenChange={setIsPickerOpen}
                                                    />

                                                    <div className={styles.PecDivider} />
                                                </>
                                            )}

                                            {isPecStep && !isPartnerFacilityStep && !isStepConfigured && (
                                                <StepEmptyState onAdd={() => setIsPickerOpen(true)} />
                                            )}

                                            {/* Partner facility step: both sections in one card, headed by the page's own text */}
                                            {isPartnerFacilityStep ? (
                                                <div className={styles.PecCard}>
                                                    <div className={styles.PecCardHeader}>
                                                        <span className={styles.PecCardTitle}>{currentConfig?.text}</span>
                                                    </div>
                                                    {visibleSections.map((section: any, index: number) => (
                                                        <FormSection
                                                            key={visibleSectionIds[index]}
                                                            setDisablePrimary={sectionDisablePrimarySetter(visibleSectionIds[index])}
                                                            section={section}
                                                            hideSectionHeading
                                                            step={step}
                                                            sectionIndex={index}
                                                            totalSteps={formConfig.length}
                                                            formData={formData}
                                                            handleChange={handleChange}
                                                            {...formSectionPassThroughProps}
                                                        />
                                                    ))}
                                                </div>
                                            ) : (
                                                /* Render each "section" inside this step */
                                                visibleSections.map((section: any, index: number) => {
                                                    const isPickerControlled = isPecStep && section === pecStepSection;
                                                    const sectionId = visibleSectionIds[index];
                                                    const formSection = (
                                                        <FormSection
                                                            key={sectionId}
                                                            setDisablePrimary={sectionDisablePrimarySetter(sectionId)}
                                                            section={
                                                                isPickerControlled && pecFilteredSection ? pecFilteredSection : section
                                                            }
                                                            hideSectionHeading={isPickerControlled}
                                                            onClearField={isPickerControlled ? handleClearField : undefined}
                                                            step={step}
                                                            sectionIndex={index}
                                                            totalSteps={formConfig.length}
                                                            formData={formData}
                                                            savedFormData={originalFormData}
                                                            handleChange={handleChange}
                                                            dealDrawerState={dealDrawerState}
                                                            lenderList={lenderList}
                                                            loanTypeOptions={loanTypeOptions}
                                                            sectorList={sectorList}
                                                            negativeSectorList={negativeSectorList}
                                                            source={source}
                                                            auditedfinancialsMapping={auditedfinancialsMapping}
                                                            isWebsiteValid={isWebsiteValid}
                                                            isWebsitePrefilled={isWebsitePrefilled}
                                                            setIsWebsiteValid={setIsWebsiteValid}
                                                            getInputValidation={getInputValidation}
                                                            getButtonIcon={getButtonIcon}
                                                            getButtonText={getButtonText}
                                                            validateWebsite={validateWebsite}
                                                            negativeLenderList={negativeLenderList}
                                                            connectedLenderNames={connectedLenderNames}
                                                            getConvertedValue={getConvertedValue}
                                                            citiesList={citiesList}
                                                            drawerOrgId={drawerOrgId}
                                                            activeFinancialsTab={activeFinancialsTab}
                                                            handleFinancialsTabChange={handleFinancialsTabChange}
                                                        />
                                                    );
                                                    if (!isPickerControlled) {
                                                        // A PEC step with a second, non-picker section (e.g. partner
                                                        // facility terms) needs it to read as its own card, matching
                                                        // the picker card above - `.PecCard + .PecCard` in the
                                                        // stylesheet supplies the gap, and never matches when this is
                                                        // the only section (today's only live shape).
                                                        if (!isPecStep) return formSection;
                                                        return (
                                                            <div className={styles.PecCard} key={`pec-section-${sectionId}`}>
                                                                {formSection}
                                                            </div>
                                                        );
                                                    }
                                                    return (
                                                        <div className={styles.PecCard} key={`pec-card-${sectionId}`}>
                                                            <div className={styles.PecCardHeader}>
                                                                <span className={styles.PecCardTitle}>{PEC_PICKER_LABELS.cardTitle}</span>
                                                                <Button
                                                                    type="button"
                                                                    variant="tertiary"
                                                                    text={PEC_PICKER_LABELS.clearAll}
                                                                    className={styles.PecTextButton}
                                                                    disabled={!canRemoveFields}
                                                                    onClick={handleClearAllForStep}
                                                                />
                                                            </div>
                                                            {formSection}
                                                        </div>
                                                    );
                                                })
                                            )}

                                            {isMandateApplicantFormStep && (
                                                <ApplicationFormStep
                                                    currentConfig={currentConfig}
                                                    formData={formData}
                                                    handleChange={handleChange}
                                                    assesseeOrgId={assesseeOrgId}
                                                    dealDrawerState={dealDrawerState}
                                                    existingDirectors={existingDirectors}
                                                    applicantPickerSelectedIds={applicantPickerSelectedIds}
                                                    handleApplicantPickerChange={handleApplicantPickerChange}
                                                    handleAddApplicant={handleAddApplicant}
                                                    applicantPrimaryTypeError={applicantPrimaryTypeError}
                                                    duplicateApplicantError={duplicateApplicantError}
                                                    primarySlotActive={primarySlotActive}
                                                    primaryApplicantLink={primaryApplicantLink}
                                                    clearPrimaryApplicant={clearPrimaryApplicant}
                                                    coApplicants={coApplicants}
                                                    removeCoApplicantRow={removeCoApplicantRow}
                                                    updateCoApplicantField={updateCoApplicantField}
                                                    formSectionPassThroughProps={formSectionPassThroughProps}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {params.get('updateMandate') === 'true' && (
                                <ConfigurableModal
                                    isOpen={params.get('updateMandate') === 'true'}
                                    onClose={() => {
                                        params.delete('updateMandate');
                                        setParams(params);
                                    }}
                                    heading="Update Mandate?"
                                    message="Do you want to update new terms to existing mandate?"
                                    primaryButton={{
                                        label: 'Yes, Update',
                                        style: { backgroundColor: '#6021B3' },
                                        onClick: handleUpdateMandate,
                                    }}
                                    secondaryButton={{
                                        label: 'Skip',
                                        style: {},
                                        onClick: handleSkipMandate,
                                    }}
                                />
                            )}

                            {/* Footer */}
                            {!dealDrawerState.readonly && (
                                <ManageDealDrawerFooter
                                    currentConfig={currentConfig}
                                    disablePrimary={disablePrimaryWithSections || pecStepGateDisabled}
                                    onPrimary={async () => handlePrimary()}
                                    onSecondary={handleSecondary}
                                    disableSecondary={disableSecondary}
                                    totalSteps={formConfig?.length || 1}
                                    currentStep={step}
                                    onProceed={() => {
                                        if (step < formConfig?.length) {
                                            handleStepperClick(step + 1);
                                        }
                                    }}
                                    onShowDismissalAlert={() => setShowDismissalAlert(true)}
                                />
                            )}
                        </Form>
                    ) : (
                        <LoadingSpinner className={styles.DealDrawerLoader} color="var(--primary-text-colour)" height="50px" />
                    )}
                </div>

                {/* Container for Comments Thread Portal - positioned on the right side */}
                <div id="edit-profile-comment-container"></div>
            </div>
        </SideDrawer>
    );

    return (
        <>
            {sideDrawerContent}

            {showRemoveAllConfirm && (
                <ConfigurableModal
                    isOpen={showRemoveAllConfirm}
                    onClose={() => setShowRemoveAllConfirm(false)}
                    heading={PEC_REMOVE_ALL_CONFIRM.heading}
                    message={PEC_REMOVE_ALL_CONFIRM.message}
                    primaryButton={{
                        label: PEC_REMOVE_ALL_CONFIRM.confirmLabel,
                        onClick: confirmRemoveAll,
                    }}
                    secondaryButton={{
                        label: PEC_REMOVE_ALL_CONFIRM.cancelLabel,
                        onClick: () => setShowRemoveAllConfirm(false),
                    }}
                />
            )}

            {showDismissalAlert && (
                <ConfigurableModal
                    isOpen={showDismissalAlert}
                    onClose={() => setShowDismissalAlert(false)}
                    heading="Unsaved changes"
                    message="Are you sure you want to leave without saving your changes?"
                    primaryButton={{
                        label: 'Save Changes',
                        style: { backgroundColor: 'var(--primary-text-colour)', fontWeight: '600', border: 'none' },
                        onClick: handleSaveAndClose,
                        disabled: disablePrimaryWithSections,
                    }}
                    secondaryButton={{
                        label: 'Close without saving',
                        style: { color: 'var(--primary-text-colour)', border: '1px solid var(--border-color)', fontWeight: '600' },
                        onClick: handleCloseWithoutSave,
                    }}
                />
            )}
        </>
    );
};

export default ManageDealDrawer;
